<template>
  <a-modal
    :visible="visible"
    :destroy-on-close="true"
    title="Edit Work Experience"
    ok-text="Save"
    @ok="handleUpdateWorkExperience"
    @cancel="closeModal"
  >
    <a-form layout="horizontal" data-vv-scope="edit-work-experience">
      <a-form-item
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        :validate-status="
          $validator.errors.first('edit-work-experience.company_name') ? 'error' : ''
        "
        :help="$validator.errors.first('edit-work-experience.company_name')"
        label="Company"
      >
        <a-input
          v-model="newWorkExperience.company_name"
          v-validate="'required|max:255'"
          data-vv-name="company_name"
          data-vv-as="company name"
          placeholder="Name of the company"
        />
      </a-form-item>

      <a-form-item
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        :validate-status="$validator.errors.first('edit-work-experience.title') ? 'error' : ''"
        :help="$validator.errors.first('edit-work-experience.title')"
        label="Title"
      >
        <a-input
          v-model="newWorkExperience.title"
          v-validate="'required|max:255'"
          placeholder="Title of the job"
          data-vv-name="title"
        />
      </a-form-item>

      <a-form-item
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        :validate-status="
          $validator.errors.first('edit-work-experience.description') ? 'error' : ''
        "
        :help="$validator.errors.first('edit-work-experience.description')"
        label="Description"
      >
        <a-textarea
          v-model="newWorkExperience.description"
          v-validate="'required'"
          placeholder="Description of the job"
          data-vv-name="description"
        />

        <a v-if="!disableDescriptionBot" :href="getDescriptionBotLink()" target="_blank"
          >Get help writing a description</a
        >
      </a-form-item>

      <a-form data-vv-scope="tab-skills" layout="horizontal">
        <a-form-item :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }" label="Skills">
          <a-select
            v-model="newWorkExperience.skill_ids"
            mode="multiple"
            show-search
            placeholder="Select skills"
            option-filter-prop="children"
          >
            <a-select-option
              v-for="s in skills"
              :key="s.id"
              :class="{ 'already-in': !!s.skill_id }"
            >
              {{ s.title }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>

      <a-form-item
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        :validate-status="$validator.errors.first('edit-work-experience.start_date') ? 'error' : ''"
        :help="$validator.errors.first('edit-work-experience.start_date')"
        label="Start date"
      >
        <a-date-picker
          v-validate="'required'"
          :value="parseDate(newWorkExperience.start_date)"
          :disabled-date="disableStartDate"
          data-vv-name="start_date"
          data-vv-as="start date"
          class="w-100"
          @change="(d) => onDateChange(d, 'start_date')"
        />
      </a-form-item>

      <a-form-item
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        :validate-status="$validator.errors.first('edit-work-experience.end_date') ? 'error' : ''"
        :help="$validator.errors.first('edit-work-experience.end_date')"
        label="End date"
      >
        <a-date-picker
          :value="parseDate(newWorkExperience.end_date)"
          :disabled-date="disableEndDate"
          class="w-100"
          @change="(d) => onDateChange(d, 'end_date')"
        />
      </a-form-item>

      <a-form-item
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        :validate-status="$validator.errors.first('edit-work-experience.url_link') ? 'error' : ''"
        :help="$validator.errors.first('edit-work-experience.url_link')"
        label="URL link"
      >
        <a-input
          v-model="newWorkExperience.url_link"
          v-validate="'max:255'"
          placeholder="URL link"
          data-vv-name="url_link"
          data-vv-as="link"
        />
      </a-form-item>
    </a-form>
    <a-form data-vv-scope="tab-skills" layout="horizontal">
      <a-form-item :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }" label="Roles">
        <a-select
          v-model="newWorkExperience.role_ids"
          mode="multiple"
          show-search
          placeholder="Select roles"
          option-filter-prop="children"
        >
          <a-select-option v-for="r in roles" :key="r.id" :value="r.id">
            {{ r.title }}
          </a-select-option>
        </a-select>
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, getCurrentInstance, watch, toRef } from "vue";
import { WorkExperienceInterface } from "../types";
import { SkillInterface } from "@/modules/skills/types";
import moment from "@/date";
import useFilters from "@/useFilters";
import { ProfileInterface } from "@/modules/authentication/types";
import useMixin from "@/useMixin";
import { RoleSmallInterface } from "@/modules/roles/types";

const disableDescriptionBot = import.meta.env.VITE_DISABLE_DESCRIPTION_BOT;
// Props
const props = defineProps({
  workExperience: { type: Object as () => WorkExperienceInterface, default: undefined },
  visible: { type: Boolean, default: false },
  profile: { type: Object as () => ProfileInterface, default: undefined },
  skills: { type: Array as () => Array<SkillInterface>, default: () => [] },
  roles: { type: Array as () => Array<RoleSmallInterface>, default: () => [] },
});

// Emits
const emits = defineEmits(["update-work-experience", "close"]);

// Filters
const { parseDate } = useFilters();

// Mixins
const { setObject } = useMixin();
// Instances
const instance = getCurrentInstance();
const $validator = instance?.proxy.$validator;
const skipNextRolePrefill = ref<boolean>(false);
const previousRoleIds = ref<Array<number>>([]);
const autoAddedSkillIds = ref<Set<number>>(new Set());

// Data Properties
const newWorkExperience = ref<WorkExperienceInterface>({
  id: 0,
  company_name: "",
  title: "",
  start_date: "",
  end_date: "",
  user: undefined,
  description: undefined,
  skill_ids: undefined,
  url_link: undefined,
  role_ids: undefined,
});

// Watchers
watch(toRef(props, "visible"), (newProp, oldProp) => {
  if (newProp && !oldProp) {
    skipNextRolePrefill.value = true;
    newWorkExperience.value = setObject(newWorkExperience.value, props.workExperience);
    newWorkExperience.value.id = props.workExperience.id;
    (newWorkExperience.value as any).role_ids =
      props.workExperience.roles?.map((role) => role.id) || [];
    previousRoleIds.value = ((newWorkExperience.value as any).role_ids || []) as Array<number>;
    autoAddedSkillIds.value = new Set();
  }
});

const getDateIfValid = (dateValue: string | null | undefined): string | null => {
  const today = moment().endOf("day");
  if (!dateValue) {
    return null;
  }

  const parsedDate = moment(dateValue);
  return parsedDate.isAfter(today) ? null : dateValue;
};

const getRoleSkillIds = (roleIds: Array<number>): Array<number> => {
  return Array.from(
    new Set(
      roleIds.flatMap((roleId) => {
        const role = props.roles.find((r) => r.id === roleId) as any;
        if (!role || !Array.isArray(role.skills)) {
          return [];
        }

        return role.skills
          .map((skill: any) => skill?.id)
          .filter((skillId: number | undefined) => !!skillId);
      })
    )
  );
};

const prefillFromRoles = (roleIds: Array<number>): void => {
  const selectedRoles = roleIds
    .map((roleId) => props.roles.find((role) => role.id === roleId) as any)
    .filter((role) => !!role);

  if (!selectedRoles.length) {
    return;
  }

  const firstRoleWithValue = (getValue: (role: any) => any): any => {
    for (const role of selectedRoles) {
      const value = getValue(role);
      if (value === null || value === undefined) {
        continue;
      }
      if (typeof value === "string" && value.trim() === "") {
        continue;
      }
      if (Array.isArray(value) && value.length === 0) {
        continue;
      }

      return value;
    }

    return null;
  };

  const companyName = firstRoleWithValue(
    (role) => role.project?.client?.name || role.project?.name || null
  );
  const title = firstRoleWithValue((role) => role.title || null);
  const description = firstRoleWithValue((role) => role.description || null);
  const startDate = firstRoleWithValue((role) => getDateIfValid(role.start_date));
  const endDate = firstRoleWithValue((role) => getDateIfValid(role.end_date));
  newWorkExperience.value.company_name = companyName || "";
  newWorkExperience.value.title = title || "";
  newWorkExperience.value.description = description || "";
  newWorkExperience.value.start_date = startDate;
  newWorkExperience.value.end_date = endDate;
};

watch(
  () => newWorkExperience.value.role_ids,
  (roleIds) => {
    if (skipNextRolePrefill.value) {
      skipNextRolePrefill.value = false;
      previousRoleIds.value = Array.isArray(roleIds) ? (roleIds as Array<number>) : [];
      return;
    }

    if (!Array.isArray(roleIds)) {
      return;
    }

    const currentRoleIds = roleIds as Array<number>;
    const addedRoleIds = currentRoleIds.filter((roleId) => !previousRoleIds.value.includes(roleId));

    if (currentRoleIds.length > 0) {
      prefillFromRoles(currentRoleIds);
    }

    const currentSkillIdSet = new Set(
      Array.isArray(newWorkExperience.value.skill_ids) ? newWorkExperience.value.skill_ids : []
    );

    const addedRoleSkillIds = getRoleSkillIds(addedRoleIds);
    for (const skillId of addedRoleSkillIds) {
      if (!currentSkillIdSet.has(skillId)) {
        currentSkillIdSet.add(skillId);
        autoAddedSkillIds.value.add(skillId);
      }
    }

    const remainingRoleSkillIds = new Set(getRoleSkillIds(currentRoleIds));
    for (const skillId of Array.from(autoAddedSkillIds.value)) {
      if (!remainingRoleSkillIds.has(skillId)) {
        currentSkillIdSet.delete(skillId);
        autoAddedSkillIds.value.delete(skillId);
      }
    }

    newWorkExperience.value.skill_ids = Array.from(currentSkillIdSet);
    previousRoleIds.value = currentRoleIds;
  }
);

//Component methods
const handleUpdateWorkExperience = (workExperience: WorkExperienceInterface) => {
  $validator?.validateAll("edit-work-experience").then((result: any) => {
    if (result) {
      emits("update-work-experience", newWorkExperience.value);
      closeModal();
    }
  });
};

const onDateChange = (value: any, attribute: string): void => {
  (newWorkExperience.value as any)[attribute] = value ? value.format("YYYY-MM-DD") : null;
};

// Cannot have start_date in future or after end_date.
const disableStartDate = (current: any): any => {
  const today = moment().endOf("day");
  const isAfterEndDate =
    current &&
    newWorkExperience.value.end_date &&
    current > moment(newWorkExperience.value.end_date).endOf("day");
  const isInFuture = current && moment(current).isAfter(today);
  return isAfterEndDate || isInFuture;
};

// Cannot have end_date in future or before start_date.
const disableEndDate = (current: any): any => {
  const today = moment().endOf("day");
  const isBeforeStartDate =
    current &&
    newWorkExperience.value.start_date &&
    current < moment(newWorkExperience.value.start_date).startOf("day");
  const isInFuture = current && moment(current).isAfter(today);
  return isBeforeStartDate || isInFuture;
};

const getDescriptionBotLink = (): string => {
  return `https://role-describer.ai.skry.se/?work_experience_id=${newWorkExperience.value.id}&embed=true`;
};

const closeModal = (): void => {
  emits("close");
};
</script>
